// Three anchoring looks of mockup round 056 (R1125, `ui-map-interface.md`
// §6.1.5): cards 07 CORNER BRACKETS, 08 HEX PIN and 10 CHART ANCHOR: the looks
// that frame the berth or stand a chart sign beside it.
//
// One entry per look id of `labels.toml anchor_styles` that this file owns.
// `RECIPES` holds the built cards and `PENDING` the ones still to build; the
// two lists are disjoint from those of every other recipe file, and
// `anchor-styles.test.ts` pins that. A pending look draws the AUTO look.
//
// Every FIGURE stands in the look's row of `data/presentation/anchor.toml`;
// a constant here is a PROPORTION of a figure the row carries: the quotient
// says what the card wrote, and it holds at any figure (R1120). Where the
// port had to differ from the card, R1127 says how.
//
// None of the three looks turns with the hull: the card draws them on the
// unturned canvas, so they read the place and never the heading.

import {
  TAU, WATER_INK, arcOf, disc, easeOut, flashOf, lerp, line, ring, setRing,
  solidWidth, strokeGain, thin,
  type AnchorCtx, type AnchorRecipe,
} from './anchor-recipe-kit';

// ---------------------------------------------------------------------------
// The shared arithmetic of the lane
// ---------------------------------------------------------------------------

/**
 * The landing of a SET cable, 0 to 1 over the row's `flash_sec`, eased out:
 * the card's `easeOut(span(S.k, 0, flash))`. A hull seen already SET reads 1.
 */
function landOf(c: AnchorCtx): number {
  return easeOut(1 - flashOf(c));
}

/** Where the start alpha of a landing pin or stud stands, as a share of its
 *  alpha at rest (the card's 0.4 against 1). */
const LAND_ALPHA0_K = 0.4;

// ---------------------------------------------------------------------------
// Card 07, CORNER BRACKETS
// ---------------------------------------------------------------------------

/** The SET arm, as a share of the live arm (the card's 7 against 6). */
const BR_SET_ARM_K = 7 / 6;
/** How far the landing ghost starts outside the tight frame, as a share of
 *  the arm (the card's 6 against 6). */
const BR_LAND_RUN_K = 1;
/** The landing ghost's alpha, as a share of the SET frame's (0.5 against
 *  0.95). */
const BR_LAND_ALPHA_K = 0.5 / 0.95;
/** The dotted track: a dash and a gap, as shares of the arm (the card's 1 and
 *  3 against 6). */
const BR_DASH_ON_K = 1 / 6;
const BR_DASH_OFF_K = 3 / 6;
/** The open pin hole of WEIGHING: its radius as a share of the resting pin
 *  (2.2 against 2.6), and its alpha as a share of a WEIGHING bracket's (0.8
 *  against 0.85). */
const BR_HOLE_R_K = 2.2 / 2.6;
const BR_HOLE_ALPHA_K = 0.8 / 0.85;
/** A SETTING bracket's alpha: from the first share to the second over the
 *  cable, plus the pulse, all as shares of a WEIGHING bracket's (the card's
 *  0.4, 0.9 and 0.08 against 0.85). */
const BR_SETTING_ALPHA0_K = 0.4 / 0.85;
const BR_SETTING_ALPHA1_K = 0.9 / 0.85;
const BR_PULSE_K = 0.08 / 0.85;

/** The four corners, as signs. */
const CORNERS: readonly (readonly [number, number])[] = [[-1, -1], [-1, 1], [1, -1], [1, 1]];

/**
 * The four brackets of a square of half side `h` round the hull: at each
 * corner one arm along the row and one along the column, `arm` long, toward
 * the hull when `arm` is positive and away from it when it is negative. The
 * card's `B_brackets` with butt ends. The first arm runs on past the corner
 * by half the width, so the two arms meet square and no pixel takes its ink
 * twice.
 */
function brackets(c: AnchorCtx, h: number, arm: number, w: number, a: number): void {
  // The arms meet square at the DRAWN width, which `thin` sets (R1132).
  const d = arm < 0 ? -1 : 1, ext = d * thin(w) / 2;
  for (const [sx, sy] of CORNERS) {
    const cx = c.x + sx * h, cy = c.y + sy * h;
    line(c, c.x + sx * (h - arm), cy, c.x + sx * (h + ext), cy, w, c.ink, a, false);
    line(c, cx, c.y + sy * (h - ext), cx, c.y + sy * (h - arm), w, c.ink, a, false);
  }
}

/**
 * Four corner brackets make a square frame round the hull.
 *
 * SETTING: the brackets close in along four dotted diagonals from the wide
 * frame to the tight one, arms toward the hull; their place on the diagonal
 * is the share of cable out, and a faint ghost holds the tight berth. SET:
 * the brackets sit tight and heavy, and a pin holds each corner; the pins
 * land large and faint and settle. WEIGHING: the brackets turn inside out,
 * arms away from the hull, and move out, and four open pin holes stay at the
 * tight corners.
 */
function cornerBrackets(c: AnchorCtx): void {
  const r = c.row, a = c.a, g = strokeGain(c);
  const tight = r.r2Px;
  if (c.phase === 'set') {
    const land = landOf(c);
    brackets(c, tight, r.lengthPx * BR_SET_ARM_K, r.widthPx * g, r.alpha * a);
    const pr = lerp(r.length2Px, r.length3Px, land) * g;
    const pa = lerp(LAND_ALPHA0_K, 1, land) * a;
    for (const [sx, sy] of CORNERS) disc(c, c.x + sx * tight, c.y + sy * tight, pr, c.ink, pa);
    if (land < 1) {
      brackets(c, tight + r.lengthPx * BR_LAND_RUN_K * land, r.lengthPx, r.width3Px * g,
        r.alpha * BR_LAND_ALPHA_K * (1 - land) * a);
    }
    return;
  }
  const out = c.phase === 'weighing';
  const wide = r.rPx, h = lerp(wide, tight, c.p);
  // The track: one dotted diagonal a corner, from the tight berth to the
  // wide start. The card's dots have round caps; the batch dashes square, so
  // each dash takes the two caps into its length and out of its gap.
  const tw = r.width3Px * g;
  const on = r.lengthPx * BR_DASH_ON_K + tw, off = Math.max(0, r.lengthPx * BR_DASH_OFF_K - tw);
  for (const [sx, sy] of CORNERS) {
    line(c, c.x + sx * tight, c.y + sy * tight, c.x + sx * wide, c.y + sy * wide,
      tw, c.ink, r.alpha3 * a, false, on, off);
  }
  if (out) {
    for (const [sx, sy] of CORNERS) {
      ring(c, c.x + sx * tight, c.y + sy * tight, r.length3Px * BR_HOLE_R_K * g,
        r.width3Px * g, c.ink, r.alpha2 * BR_HOLE_ALPHA_K * a);
    }
    brackets(c, h, -r.lengthPx, r.width2Px * g, r.alpha2 * a);
    return;
  }
  brackets(c, tight, r.lengthPx, r.width3Px * g, r.alpha4 * a);
  const pulse = 0.5 + 0.5 * Math.sin(TAU * r.speed * c.t + c.seed);
  const la = r.alpha2 * (lerp(BR_SETTING_ALPHA0_K, BR_SETTING_ALPHA1_K, c.p) + BR_PULSE_K * pulse);
  brackets(c, h, r.lengthPx, r.width2Px * g, la * a);
}

// ---------------------------------------------------------------------------
// Card 08, HEX PIN
// ---------------------------------------------------------------------------

/** The backing hex of a moving cable, as a share of the SETTING side's
 *  width (the card's 1 against 2). */
const HEX_BACK_W_K = 1 / 2;
/** The dashes of a WEIGHING side, as shares of the open ring's radius (the
 *  card's 3.2 and 2.6 against 3). */
const HEX_DASH_ON_K = 3.2 / 3;
const HEX_DASH_OFF_K = 2.6 / 3;
/** The open ring's stroke, as a share of the WEIGHING side's (1.3 against
 *  1.6). */
const HEX_HOLE_W_K = 1.3 / 1.6;
/** How far the solid head of a SETTING side swells with its pulse, as a
 *  share of its radius (the card's 0.12). */
const HEX_PULSE_K = 0.12;

/** The seven vertices of the unit hex, clockwise from the top vertex, the
 *  last one the first again: the card's `B_hexLine`. */
const HEX_UNIT: readonly (readonly [number, number])[] = Array.from({ length: 7 }, (_, k) => {
  const a = (60 * k - 90) * Math.PI / 180;
  return [Math.cos(a), Math.sin(a)] as const;
});

/** Where the free end of a partial hex stands. The frame path reuses it. */
const hexEnd = { x: 0, y: 0 };

/**
 * The first `share` of the hex outline of radius `rad` round the hull,
 * clockwise from the top vertex: one stroke a side. It leaves the free end
 * in `hexEnd` and answers false when nothing is out.
 */
function hexPath(
  c: AnchorCtx, rad: number, share: number, w: number, a: number, round: boolean,
  dashOn = 0, dashOff = 0,
): boolean {
  if (share <= 0) return false;
  const sides = Math.min(6, share * 6);
  for (let k = 0; k < 6 && k < sides; k++) {
    const f = Math.min(1, sides - k);
    const [ux0, uy0] = HEX_UNIT[k], [ux1, uy1] = HEX_UNIT[k + 1];
    const x0 = c.x + ux0 * rad, y0 = c.y + uy0 * rad;
    hexEnd.x = x0 + (ux1 - ux0) * rad * f;
    hexEnd.y = y0 + (uy1 - uy0) * rad * f;
    line(c, x0, y0, hexEnd.x, hexEnd.y, w, c.ink, a, round, dashOn, dashOff);
  }
  return true;
}

/**
 * The six sides of the hex under the hull; no ring.
 *
 * SETTING: the sides draw one after another, clockwise from the top vertex,
 * over a faint backing hex; the share of cable out is sides out of six, and
 * the side that grows carries a solid head that pulses. SET: the whole hex,
 * heavy, with a stud on each vertex; the studs land large and faint and
 * settle. WEIGHING: the sides leave in the reverse order, the studs are
 * gone, the line is dashed and its free end carries an open ring.
 */
function hexPin(c: AnchorCtx): void {
  const r = c.row, a = c.a, g = strokeGain(c), rad = r.rPx;
  if (c.phase === 'set') {
    const land = landOf(c);
    hexPath(c, rad, 1, r.widthPx * g, r.alpha * a, true);
    const sr = lerp(r.lengthPx, r.length2Px, land) * g;
    const sa = lerp(LAND_ALPHA0_K, 1, land) * a;
    for (let k = 0; k < 6; k++) disc(c, c.x + HEX_UNIT[k][0] * rad, c.y + HEX_UNIT[k][1] * rad, sr, c.ink, sa);
    return;
  }
  const out = c.phase === 'weighing';
  hexPath(c, rad, 1, r.width2Px * HEX_BACK_W_K * g, r.alpha3 * a, false);
  const drawn = out
    ? hexPath(c, rad, c.p, r.width3Px * g, r.alpha2 * a, false,
      r.r2Px * HEX_DASH_ON_K, r.r2Px * HEX_DASH_OFF_K)
    : hexPath(c, rad, c.p, r.width2Px * g, r.alpha2 * a, true);
  if (!drawn) return;
  if (out) {
    disc(c, hexEnd.x, hexEnd.y, r.r2Px * g, WATER_INK, 1);
    // The ring takes the card's 0.95, the SET hex's own alpha.
    ring(c, hexEnd.x, hexEnd.y, r.r2Px * g, r.width3Px * HEX_HOLE_W_K * g, c.ink, r.alpha * a);
  } else {
    const swell = 1 + HEX_PULSE_K * Math.sin(TAU * r.speed * c.t + c.seed);
    disc(c, hexEnd.x, hexEnd.y, r.length3Px * g * swell, c.ink, a);
  }
}

// ---------------------------------------------------------------------------
// Card 10, CHART ANCHOR
// ---------------------------------------------------------------------------
//
// The glyph stands in the card's own px against its plate of radius 10.5,
// so every point below is a share of `r_px`: a row that grows the plate
// grows the glyph with it.

/** The card's plate radius, the unit of the glyph table. */
const GL_PLATE = 10.5;
/** Where the plate's centre stands under the glyph's, in glyph px. */
const GL_PLATE_DY = 0.5;
/** Where the SET ground bar stands under the glyph's centre, in glyph px. */
const GL_GROUND_Y = 10.4;
/** The chevron: its half span and its half height, in glyph px. */
const GL_CHEV_HALF = 3.2;
const GL_CHEV_RISE = 1.6;
/** The ghost stroke, as a share of the WEIGHING stroke (1 against 1.2). */
const GL_GHOST_W_K = 1 / 1.2;
/** The head of a SETTING stroke, as a share of the SET ring (1.5 against
 *  2.1). */
const GL_HEAD_R_K = 1.5 / 2.1;
/** The ground bar: its stroke as a share of the SET glyph's (2 against 1.8),
 *  its alpha as a share of the SET glyph's (0.95 against 0.98). */
const GL_GROUND_W_K = 2 / 1.8;
const GL_GROUND_A_K = 0.95 / 0.98;
/** How far the chevron bobs, as a share of its height over the glyph (0.8
 *  against 13.6). */
const GL_BOB_K = 0.8 / 13.6;
/** The chevron's stroke, as a share of the SETTING stroke (1.6 against 1.7);
 *  the second WEIGHING chevron's stroke (1.3 against 1.7) and alpha (0.5
 *  against 0.95). */
const GL_CHEV_W_K = 1.6 / 1.7;
const GL_CHEV2_W_K = 1.3 / 1.7;
const GL_CHEV2_A_K = 0.5 / 0.95;

/** One stroke of the glyph path: an arc of the circle batch, or a line. */
type GlyphPiece =
  | { readonly arc: true; readonly cx: number; readonly cy: number; readonly r: number;
    readonly a0: number; readonly sweep: number; readonly dir: 1 | -1; readonly len: number }
  | { readonly arc: false; readonly x0: number; readonly y0: number;
    readonly x1: number; readonly y1: number; readonly len: number };

function seg(x0: number, y0: number, x1: number, y1: number): GlyphPiece {
  return { arc: false, x0, y0, x1, y1, len: Math.hypot(x1 - x0, y1 - y0) };
}

function arcPiece(cx: number, cy: number, r: number, a0: number, sweep: number, dir: 1 | -1): GlyphPiece {
  return { arc: true, cx, cy, r, a0, sweep, dir, len: r * sweep };
}

/** The arms: the arc of radius 6.2 about (0, 1.2), from 0.92π down to
 *  0.08π, and its two ends, where the flukes start. */
const ARM_R = 6.2, ARM_CY = 1.2, ARM_A0 = 0.92 * Math.PI, ARM_SWEEP = 0.84 * Math.PI;
const ARM_L = { x: Math.cos(ARM_A0) * ARM_R, y: ARM_CY + Math.sin(ARM_A0) * ARM_R };
const ARM_R_END = {
  x: Math.cos(ARM_A0 - ARM_SWEEP) * ARM_R, y: ARM_CY + Math.sin(ARM_A0 - ARM_SWEEP) * ARM_R,
};

/**
 * The card's glyph in its trace order: ring, shank, stock, arms, the left
 * fluke and the right one. The ring runs clockwise from its foot and the
 * arms from left to right, as the card's polylines do (R1127).
 */
const GLYPH: readonly GlyphPiece[] = [
  arcPiece(0, -6.5, 2, Math.PI / 2, TAU, 1),
  seg(0, -4.5, 0, 7.4),
  seg(-3.6, -2.2, 3.6, -2.2),
  arcPiece(0, ARM_CY, ARM_R, ARM_A0, ARM_SWEEP, -1),
  seg(ARM_L.x + 2.6, ARM_L.y + 0.6, ARM_L.x, ARM_L.y),
  seg(ARM_L.x, ARM_L.y, ARM_L.x - 0.4, ARM_L.y + 2.8),
  seg(ARM_R_END.x - 2.6, ARM_R_END.y + 0.6, ARM_R_END.x, ARM_R_END.y),
  seg(ARM_R_END.x, ARM_R_END.y, ARM_R_END.x + 0.4, ARM_R_END.y + 2.8),
];
const GLYPH_LEN = GLYPH.reduce((n, g) => n + g.len, 0);
/** The ring's centre, where a SET glyph puts its solid ring. */
const GL_RING = { x: 0, y: -6.5 };

/**
 * The open side of each fluke, from its outer tip to its lower tip, and the
 * unit normal that points into the fluke. A SET glyph fills the fluke with a
 * stroke along it (R1127).
 */
const FLUKE_SIDES = [[4, 5], [6, 7]].map(([i, j]) => {
  const a = GLYPH[i] as Extract<GlyphPiece, { arc: false }>;
  const b = GLYPH[j] as Extract<GlyphPiece, { arc: false }>;
  const ax = a.x0, ay = a.y0, bx = b.x1, by = b.y1, vx = a.x1, vy = a.y1;
  const len = Math.hypot(bx - ax, by - ay);
  let nx = -(by - ay) / len, ny = (bx - ax) / len;
  if ((vx - ax) * nx + (vy - ay) * ny < 0) { nx = -nx; ny = -ny; }
  return { ax, ay, bx, by, nx, ny };
});

/** Where the glyph stands and how large it is this frame. The one context
 *  the path helpers read. */
const gl = { x: 0, y: 0, u: 1, endX: 0, endY: 0 };

/**
 * The first `share` of the glyph path, by length, as the card's `B_partial`
 * traces it. It leaves the free end in `gl.endX`, `gl.endY` and answers
 * false when nothing is out.
 */
function glyphPath(c: AnchorCtx, share: number, w: number, a: number): boolean {
  let left = GLYPH_LEN * Math.min(1, share);
  if (left <= 1e-6) return false;
  const u = gl.u;
  for (const g of GLYPH) {
    if (left <= 1e-6) break;
    const f = Math.min(1, left / g.len);
    left -= g.len;
    if (g.arc) {
      const sw = g.sweep * f, cx = gl.x + g.cx * u, cy = gl.y + g.cy * u, r = g.r * u;
      const start = g.dir > 0 ? g.a0 : g.a0 - sw;
      arcOf(c, cx, cy, r, w, start, sw, c.ink, a);
      const end = g.a0 + g.dir * sw;
      gl.endX = cx + Math.cos(end) * r;
      gl.endY = cy + Math.sin(end) * r;
    } else {
      const x0 = gl.x + g.x0 * u, y0 = gl.y + g.y0 * u;
      gl.endX = x0 + (g.x1 - g.x0) * u * f;
      gl.endY = y0 + (g.y1 - g.y0) * u * f;
      line(c, x0, y0, gl.endX, gl.endY, w, c.ink, a);
    }
  }
  return true;
}

/** A chevron over the glyph at world height `y` over its centre, its point
 *  down when `dir` is 1 and up when it is −1: the card's `B_chevron`. */
function chevron(c: AnchorCtx, y: number, dir: number, w: number, a: number): void {
  const h = GL_CHEV_HALF * gl.u, d = GL_CHEV_RISE * gl.u * dir;
  const x = gl.x, cy = gl.y + y;
  line(c, x - h, cy - d, x, cy + d, w, c.ink, a);
  line(c, x, cy + d, x + h, cy - d, w, c.ink, a);
}

/**
 * A small chart anchor at a fixed offset below and right of the hull, on a
 * dark plate that keeps it clear of the grid and the claim border.
 *
 * SETTING: the glyph draws stroke by stroke over a faint ghost, ring, shank,
 * stock, arms and flukes; the share of the path drawn is the share of cable
 * out, the free end carries a solid head, and a chevron over the glyph
 * points down. SET: the whole glyph, heavy, with solid flukes, a solid ring
 * and a ground bar under the crown that opens over `flash_sec`. WEIGHING:
 * the glyph erases in the reverse order in a thin line, and two chevrons
 * point up. The glyph grows by `1 / zoom^grow` when the map zooms out past 1.
 */
function chartAnchor(c: AnchorCtx): void {
  setRing(c);
  const r = c.row, a = c.a;
  const k = c.z < 1 && r.grow > 0 ? Math.pow(c.z, -r.grow) : 1;
  gl.x = c.x + r.xPx;
  gl.y = c.y + r.yPx;
  gl.u = k * r.rPx / GL_PLATE;
  // The plate is water, and water takes no faction alpha.
  disc(c, gl.x, gl.y + GL_PLATE_DY * gl.u, r.rPx * k, WATER_INK, r.alpha4);
  if (c.phase === 'set') {
    const land = landOf(c), w = r.widthPx * k;
    glyphPath(c, 1, w, r.alpha * a);
    disc(c, gl.x + GL_RING.x * gl.u, gl.y + GL_RING.y * gl.u, r.r2Px * k, c.ink, a);
    for (const f of FLUKE_SIDES) {
      const ox = f.nx * w / 4, oy = f.ny * w / 4;
      line(c, gl.x + f.ax * gl.u + ox, gl.y + f.ay * gl.u + oy,
        gl.x + f.bx * gl.u + ox, gl.y + f.by * gl.u + oy, solidWidth(w / 2), c.ink, a, false);
    }
    const half = r.lengthPx * k * land, gy = gl.y + GL_GROUND_Y * gl.u;
    line(c, gl.x - half, gy, gl.x + half, gy, w * GL_GROUND_W_K, c.ink,
      r.alpha * GL_GROUND_A_K * a);
    return;
  }
  const out = c.phase === 'weighing';
  glyphPath(c, 1, r.width3Px * GL_GHOST_W_K * k, r.alpha3 * a);
  if (glyphPath(c, c.p, (out ? r.width3Px : r.width2Px) * k, r.alpha2 * a) && !out) {
    disc(c, gl.endX, gl.endY, r.r2Px * GL_HEAD_R_K * k, c.ink, a);
  }
  const bob = Math.sin(TAU * r.speed * c.t + c.seed) * r.length2Px * GL_BOB_K * k;
  const y = -r.length2Px * k;
  chevron(c, y + (out ? -bob : bob), out ? -1 : 1, r.width2Px * GL_CHEV_W_K * k, r.alpha2 * a);
  if (out) {
    chevron(c, y - GL_CHEV_HALF * gl.u - bob, -1, r.width2Px * GL_CHEV2_W_K * k,
      r.alpha2 * GL_CHEV2_A_K * a);
  }
}

/** The built looks of this file, by id. */
export const RECIPES: Readonly<Record<string, AnchorRecipe>> = {
  'corner-brackets': cornerBrackets,
  'hex-pin': hexPin,
  'chart-anchor': chartAnchor,
};

/** The looks of this file still to build. They draw the AUTO look. */
export const PENDING: readonly string[] = [];
